import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { BarChart3, History, Phone } from "lucide-react";
import { endCallSession, endCallSessionBeacon } from "@/api/callCenter";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ActiveSession } from "@/components/callcenter/ActiveSession";
import { CallHistoryTab } from "@/components/callcenter/CallHistoryTab";
import { CallStatsTab } from "@/components/callcenter/CallStatsTab";
import { SessionSetup } from "@/components/callcenter/SessionSetup";
import { SessionSummary } from "@/components/callcenter/SessionSummary";
import type { CallSession } from "@/components/callcenter/types";
import { FeatureDisabled } from "@/components/layout/FeatureDisabled";
import { useFeature } from "@/hooks/useFeature";
import { usePermissions } from "@/hooks/usePermissions";

type Tab = "session" | "history" | "stats";

/** Route entry point — three tabs: the live Session flow (setup →
 *  active → summary), the call History table, and the Stats
 *  dashboard.
 *
 *  All three surfaces stay mounted at once; switching tabs only
 *  toggles a `hidden` class. That's deliberate: the session carries
 *  an in-memory queue + per-call results, so unmounting it on a tab
 *  switch would drop the operator back to setup. Keeping every tab
 *  mounted means History / Stats can be visited mid-session and the
 *  session resumes exactly where it was left.
 *
 *  The session is still component-local state — a full page reload
 *  drops the queue back to setup (the call timer has its own
 *  persisted store; the queue does not). */
export default function CallCenterPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { canViewCallCenterStats } = usePermissions();
  const enabled = useFeature("call_center");
  const [tab, setTab] = useState<Tab>("session");

  // Defensive: if the role drops view_stats while the user is on
  // the Stats tab (live perms refresh), flip back to Session so
  // they don't sit on a hidden surface.
  useEffect(() => {
    if (tab === "stats" && !canViewCallCenterStats) setTab("session");
  }, [tab, canViewCallCenterStats]);

  if (!enabled) {
    return (
      <FeatureDisabled icon={Phone} title="Call Center is disabled" />
    );
  }

  return (
    <div className="space-y-6 pb-24">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Call Center
          </h1>
          <p className="text-sm text-slate-500">
            Power-call your queue and watch the numbers move
          </p>
        </div>
        <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
          <TabsList>
            <TabsTrigger value="session">
              <Phone className="mr-1.5 h-4 w-4" />
              Session
            </TabsTrigger>
            <TabsTrigger value="history">
              <History className="mr-1.5 h-4 w-4" />
              History
            </TabsTrigger>
            {canViewCallCenterStats && (
              <TabsTrigger value="stats">
                <BarChart3 className="mr-1.5 h-4 w-4" />
                Stats
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>
      </header>

      {/* Every tab stays mounted — only visibility toggles — so
       *  flipping to History / Stats and back never tears down the
       *  in-progress session. */}
      <div className={tab === "session" ? "" : "hidden"}>
        <CallSessionTab slug={slug} active={tab === "session"} />
      </div>
      <div className={tab === "history" ? "" : "hidden"}>
        <CallHistoryTab slug={slug} />
      </div>
      {canViewCallCenterStats && (
        <div className={tab === "stats" ? "" : "hidden"}>
          <CallStatsTab slug={slug} />
        </div>
      )}
    </div>
  );
}

/** Three-state dispatcher: setup → active → summary. Session state
 *  is local because it carries the in-memory queue + per-call results
 *  list, which we don't want to round-trip through the URL or storage.
 *  This component is never unmounted by a tab switch (see
 *  CallCenterPage), so the state survives the operator stepping away
 *  to the History / Stats tabs. */
function CallSessionTab({ slug, active }: { slug: string; active: boolean }) {
  const [session, setSession] = useState<CallSession | null>(null);
  const [sessionDone, setSessionDone] = useState(false);
  // True once startCallSession has locked leads to this operator and
  // they haven't been released yet. Gates the cleanup paths below so we
  // never fire a release for a session that was never opened.
  const lockedRef = useRef(false);

  // Release whatever's still locked to this operator (leads they
  // skipped or never reached — processed ones free themselves when
  // their pipeline status changes). The ref guard plus the idempotent
  // server endpoint make double-calls harmless.
  const releaseLocks = useCallback(() => {
    if (!lockedRef.current) return;
    lockedRef.current = false;
    endCallSession(slug).catch(() => {});
  }, [slug]);

  // Tab close / hard navigation: fire a keepalive release while the
  // page tears down, otherwise the operator's locks would linger until
  // their server-side TTL. `pagehide` over `beforeunload` — it also
  // covers the mobile / bfcache paths beforeunload misses, and we want
  // the side effect, not a "leave site?" confirmation prompt.
  useEffect(() => {
    const handleUnload = () => {
      if (lockedRef.current) endCallSessionBeacon(slug);
    };
    window.addEventListener("pagehide", handleUnload);
    return () => window.removeEventListener("pagehide", handleUnload);
  }, [slug]);

  // In-app navigation away from the Call Center unmounts this tab and
  // drops the in-memory queue — release the locks along with it.
  useEffect(() => () => releaseLocks(), [releaseLocks]);

  function handleStart(next: CallSession) {
    lockedRef.current = true;
    setSession(next);
  }

  function handleEnd() {
    // Operator is done dialing — free any leads they skipped or never
    // reached before flipping to the (purely in-memory) summary.
    releaseLocks();
    setSessionDone(true);
  }

  if (sessionDone && session) {
    return (
      <SessionSummary
        session={session}
        onClose={() => {
          setSession(null);
          setSessionDone(false);
        }}
      />
    );
  }
  if (session) {
    const updateSession: (
      next: CallSession | ((prev: CallSession) => CallSession),
    ) => void = (next) => {
      setSession((prev) => {
        if (!prev) return prev;
        return typeof next === "function"
          ? (next as (p: CallSession) => CallSession)(prev)
          : next;
      });
    };
    return (
      <ActiveSession
        slug={slug}
        active={active}
        session={session}
        setSession={updateSession}
        onEnd={handleEnd}
      />
    );
  }
  return <SessionSetup slug={slug} onStart={handleStart} />;
}
